import { useEffect, useState } from "react";
import {
  useRightSidebarContent,
  useSidebar,
} from "@capacit-com/lara-law-shared-ui";

export const CONTENT_SCROLL_SELECTOR = '[data-content-scroll="true"]';
export const FAB_EDGE_MARGIN_PX = 24;

export type ContentScrollFabBounds = {
  centerX: number;
  rightInset: number;
};

export const measureContentScrollFabBounds = (
  element: Element | null,
  viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0
): ContentScrollFabBounds | null => {
  if (!element || !(element instanceof HTMLElement)) return null;

  const rect = element.getBoundingClientRect();
  if (rect.width <= 0) return null;

  return {
    centerX: rect.left + rect.width / 2,
    rightInset: viewportWidth - rect.right + FAB_EDGE_MARGIN_PX,
  };
};

/**
 * Tracks the horizontal center and right inset of a layout element so fixed FABs
 * stay aligned when sidebars open, collapse, or resize.
 *
 * When `overrideElement` is set, that element is measured instead of the main
 * content scroll area (e.g. document viewer column beside an instruction panel).
 */
export const useContentScrollFabBounds = (
  overrideElement?: HTMLElement | null
): ContentScrollFabBounds | null => {
  const [bounds, setBounds] = useState<ContentScrollFabBounds | null>(null);
  const { open: leftSidebarOpen, state: leftSidebarState } = useSidebar("left");
  const { open: rightSidebarOpen } = useSidebar("right");
  const { content: rightSidebarContent } = useRightSidebarContent();

  useEffect(() => {
    const element =
      overrideElement !== undefined
        ? overrideElement
        : document.querySelector<HTMLElement>(CONTENT_SCROLL_SELECTOR);
    if (!element) {
      if (overrideElement !== undefined) {
        setBounds(null);
      }
      return;
    }

    const update = () => {
      const next = measureContentScrollFabBounds(element);
      setBounds((prev) => {
        if (
          prev &&
          next &&
          prev.centerX === next.centerX &&
          prev.rightInset === next.rightInset
        ) {
          return prev;
        }
        return next;
      });
    };

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    window.addEventListener("resize", update);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [
    overrideElement,
    leftSidebarOpen,
    leftSidebarState,
    rightSidebarOpen,
    rightSidebarContent,
  ]);

  return bounds;
};
